import { useEffect, useRef, useState, type JSX } from 'react';
import RatingsBarChart from '@/pages/user/components/RatingsBarChart';
import {
  useLazyGetAdminCurrentWeekPredictionQuery,
  useLazyGetAdminNextWeekPredictionQuery,
  useLazyGetPredictionByPeriodQuery,
} from '@/store/api/mePredicionWeekly/mepredictionapi';

import AudioVisualizer from '@/pages/user/components/AudioVisualizer';
import { useLazyGetPredictionAudioQuery } from '@/store/api/PredictionAudio/predictionaudioApi';
import Loader from '@/components/Loader';
import Modal from '@/utils/modal.utils';
import { SegmentedControl } from '@/components/ui/SegmentedControl';

interface WeeklyPredictionProps {
  fetchedId: string;
  fetchUserType?: string;
  apiData: any;
  receivedResponse: boolean;
  language: string;

  rashiCharts?: {
    south?: string;
    north?: string;
  };
}

interface HoroscopeData {
  title: string;
  content: string;
}

interface Ratings {
  overall: number;
  wealth_investments: number;
  career_profession: number;
  education: number;
  health: number;
  relationships: number;
  children: number;
}

// interface WeeklyPeriodData {
//   charts?: {
//     chartType: 'south' | 'north';
//     imageUrl: string;
//   }[];
//   prediction?: string;
//   id?: string;
// }

const formatWeeklyPrediction = (raw?: string): HoroscopeData[] => {
  if (!raw) return [];

  const normalizeTitle = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const cleanBullet = (text: string) => text.replace(/^[•\-–]\s*/, '').trim();

  try {
    const parsed = JSON.parse(raw);
    const formatted: HoroscopeData[] = [];

    const heading =
      parsed.heading ||
      parsed.overview?.heading ||
      parsed.general_predictions?.heading ||
      (parsed.prediction &&
        (parsed.prediction.heading ||
          parsed.prediction.overview?.heading ||
          parsed.prediction.general_predictions?.heading));

    if (heading) {
      formatted.push({
        title: 'HEADING',
        content: heading,
      });
    }

    Object.entries(parsed).forEach(([key, value]: any) => {
      if (key === 'ratings' || key === 'heading') return;

      if (
        (key === 'overview' || key === 'general_predictions') &&
        typeof value === 'object' &&
        value !== null
      ) {
        Object.entries(value).forEach(([subKey, subValue]: any) => {
          if (subKey === 'heading') return;
          formatted.push({
            title: normalizeTitle(subKey),
            content: typeof subValue === 'string' ? subValue : JSON.stringify(subValue),
          });
        });
        return;
      }

      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        key !== 'key_days'
      ) {
        formatted.push({ title: normalizeTitle(key), content: '' }); // Parent Header
        Object.entries(value).forEach(([subKey, subValue]: any) => {
          formatted.push({
            title: normalizeTitle(subKey),
            content: typeof subValue === 'string' ? subValue : JSON.stringify(subValue),
          });
        });
        return;
      }

      if (key === 'key_days' && value) {
        formatted.push({
          title: 'KEY DAYS',
          content: JSON.stringify({
            type: 'grouped-bullets',
            groups: [
              value.favourable_days?.length
                ? {
                    subtitle: 'Favourable Days',
                    items: value.favourable_days.map(cleanBullet),
                  }
                : null,
              value.caution_days?.length
                ? {
                    subtitle: 'Caution Days',
                    items: value.caution_days.map(cleanBullet),
                  }
                : null,
            ].filter(Boolean),
          }),
        });
        return;
      }

      if (Array.isArray(value)) {
        formatted.push({
          title: normalizeTitle(key),
          content: JSON.stringify({
            type: 'bullets',
            items: value.map(cleanBullet),
          }),
        });
        return;
      }

      if (typeof value === 'string') {
        formatted.push({
          title: normalizeTitle(key),
          content: value,
        });
        return;
      }

      if (value?.title && value?.content) {
        formatted.push({
          title: value.title,
          content:
            typeof value.content === 'string' ? value.content : JSON.stringify(value.content),
        });
      }
    });

    return formatted;
  } catch (e) {
    console.error('Weekly prediction parse error', e);
    return [];
  }
};

export default function WeeklyPrediction({
  fetchedId,
  receivedResponse,
  rashiCharts,
  fetchUserType,
  language,
}: WeeklyPredictionProps): JSX.Element {
  const isGuestUser = fetchUserType === 'Guest';
  console.log('usertype', fetchUserType);
  const [ratings, setRatings] = useState<Ratings | null>(null);
  const [weeklyPrediction, setWeeklyPrediction] = useState<HoroscopeData[]>([]);
  const [predictionCharts, setPredictionCharts] = useState<{
    south?: string;
    north?: string;
  } | null>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isWeeklyUILoading = loading || predictionCharts === null;
  const [weeklyView, setWeeklyView] = useState<'current' | 'next'>('current');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [textStatus, setTextStatus] = useState<'PENDING' | 'SUCCESS' | 'FAILED' | ''>('');
  const [chartsLoading, setChartsLoading] = useState(true);
  const [predictionId, setPredictionId] = useState<string | null>(null);

  const [triggerCurrentWeek] = useLazyGetAdminCurrentWeekPredictionQuery();

  const [triggerNextWeek] = useLazyGetAdminNextWeekPredictionQuery();

  const [triggerPredictionPeriod] = useLazyGetPredictionByPeriodQuery();
  const [triggerAudio] = useLazyGetPredictionAudioQuery();
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    };
  }, []);

  const fetchAudioWithPolling = async (predictionId: string, retryCount = 0) => {
    if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    setAudioLoading(true);
    setAudioError(null);
    try {
      const audioRes = await triggerAudio({
        predictionId,
        language: 'English',
      }).unwrap();

      const rawStatus = audioRes?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (audioRes?.url) {
        setAudioSrc(audioRes.url);
        setAudioLoading(false);
        return;
      }

      if (status === 'DRAFT') {
        setAudioError(audioRes?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // If pending or no url, poll after 2 seconds
      audioPollingTimeoutRef.current = setTimeout(
        () => fetchAudioWithPolling(predictionId, retryCount),
        2000
      );
    } catch (err: any) {
      console.error('Audio fetch error:', err);

      const rawStatus = err?.data?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (status === 'DRAFT') {
        setAudioError(err?.data?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // Handle server-side 500 error or other failed generations
      if (
        err?.status === 500 ||
        rawStatus === 500 ||
        String(err?.data?.message || '')
          .toLowerCase()
          .includes('fail')
      ) {
        setAudioError(err?.data?.message || 'Audio failed to generate.');
        setAudioLoading(false);
        return;
      }

      if (retryCount < 5) {
        audioPollingTimeoutRef.current = setTimeout(
          () => fetchAudioWithPolling(predictionId, retryCount + 1),
          2000
        );
      } else {
        setAudioError(err?.data?.message || 'Audio generation failed. Please retry.');
        setAudioLoading(false);
      }
    }
  };

  // --helper
  //   function ordinal(day:number) {
  //   if (day > 3 && day < 21) return `${day}th`;
  //   switch (day % 10) {
  //     case 1: return `${day}st`;
  //     case 2: return `${day}nd`;
  //     case 3: return `${day}rd`;
  //     default: return `${day}th`;

  //   }
  // }

  //   function formatDateRange(title:string) {
  //   // extract date strings between "for" and "to"
  //   const match = title.match(/for (.*?) to (.*)/);

  //   if (!match) return "";

  //   const startDate = new Date(match[1]);
  //   const endDate = new Date(match[2]);

  //   const month = startDate.toLocaleString("en-US", { month: "long" });
  //   const year = startDate.getFullYear();

  //   const startDay = startDate.getDate();
  //   const endDay = endDate.getDate();

  //   return `${month} ${ordinal(startDay)} – ${ordinal(endDay)}, ${year}`;
  // }

  const openModal = (images: (string | undefined)[], title: string) => {
    setModalImages(images.filter(Boolean) as string[]);
    setSelectedTitle(title);
    setModalOpen(true);
  };

  useEffect(() => {
    return () => {
      if (audioSrc) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [audioSrc]);

  const reloadWeeklyPrediction = () => {
    setWeeklyPrediction([]);
    setRatings(null);
    setAudioSrc(null);
    setPredictionId(null);
    setTextStatus('PENDING');
  };

  useEffect(() => {
    async function fetchTransitCharts() {
      if (!receivedResponse) return;

      try {
        const period = weeklyView === 'current' ? 'CurrentWeek' : 'NextWeek';
        const res = await triggerPredictionPeriod(period).unwrap();

        if (!res?.charts) {
          setPredictionCharts(null);
          return;
        }

        setPredictionCharts({
          south: res.charts.find((c: any) => c.chartType === 'south')?.imageUrl,
          north: res.charts.find((c: any) => c.chartType === 'north')?.imageUrl,
        });
      } catch (err) {
        console.error('Transit charts fetch failed:', err);
        setPredictionCharts(null);
      }
    }

    if (!isGuestUser) {
      fetchTransitCharts();
    } else {
      setChartsLoading(false);
    }
  }, [receivedResponse, weeklyView]);

  useEffect(() => {
    setPredictionCharts(null);
  }, [weeklyView]);

  useEffect(() => {
    async function fetchMyWeeklyPredictionData() {
      if (!receivedResponse) return;

      try {
        setLoading(true);
        setTextStatus('PENDING');

        const res =
          weeklyView === 'current'
            ? await triggerCurrentWeek({ userId: fetchedId, language }).unwrap()
            : await triggerNextWeek({ userId: fetchedId, language }).unwrap();

        // --helper
        // const dateRes = formatWeeklyPrediction(res.prediction);
        // console.log("-----------",formatDateRange(dateRes[0].title));

        const rawRes = res as any;
        const container = rawRes?.prediction || rawRes?.data;

        if (!container || !container.prediction) {
          setWeeklyPrediction([]);
          setRatings(null);
          setTextStatus('FAILED');
          return;
        }

        let parsed: any;

        if (typeof container.prediction === 'string') {
          try {
            parsed = JSON.parse(container.prediction);
          } catch (e) {
            console.error('Weekly prediction JSON parse error', e);
            setRatings(null);
            setWeeklyPrediction([]);
            setTextStatus('FAILED');
            return;
          }
        } else {
          parsed = container.prediction;
        }

        if (parsed?.ratings) {
          const r = parsed.ratings;

          setRatings({
            overall: r.overall ?? 0,
            wealth_investments: r.wealth_investments ?? 0,
            career_profession: r.career_profession ?? 0,
            education: r.education ?? 0,
            health: r.health ?? 0,
            relationships: r.relationships ?? 0,
            children: r.children ?? 0,
          });
        } else {
          setRatings(null);
        }

        setWeeklyPrediction(
          formatWeeklyPrediction(
            typeof container.prediction === 'string'
              ? container.prediction
              : JSON.stringify(container.prediction)
          )
        );

        setTextStatus('SUCCESS');

        if (container?.id) {
          setPredictionId(container.id);
          fetchAudioWithPolling(container.id);
        } else {
          setAudioSrc(null);
        }
      } catch (err) {
        console.error('My weekly prediction fetch failed:', err);
        setWeeklyPrediction([]);
        setRatings(null);
        setTextStatus('FAILED');
      } finally {
        setLoading(false);
      }
    }

    fetchMyWeeklyPredictionData();
  }, [receivedResponse, weeklyView, language]);

  //   if (n > 3 && n < 21) return 'th';
  //   switch (n % 10) {
  //     case 1:
  //       return 'st';
  //     case 2:
  //       return 'nd';
  //     case 3:
  //       return 'rd';
  //     default:
  //       return 'th';
  //   }
  // };

  // const getLastDayOfMonth = (date: Date) => {
  //   return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  // };

  // const getCurrentWeekRange = () => {
  //   const now = new Date();
  //   const day = now.getDay();
  //   const diffToMonday = day === 0 ? -6 : 1 - day;

  //   const start = new Date(now);
  //   start.setDate(now.getDate() + diffToMonday);

  //   const end = new Date(start);
  //   end.setDate(start.getDate() + 6);

  //   const month = start.toLocaleString('default', { month: 'long' });
  //   const year = start.getFullYear();

  //   return `${month} ${start.getDate()}${getOrdinal(
  //     start.getDate()
  //   )} – ${end.getDate()}${getOrdinal(end.getDate())}, ${year}`;
  // };

  // const getNextWeekRange = () => {
  //   const now = new Date();
  //   const day = now.getDay();
  //   const diffToNextMonday = day === 0 ? 1 : 8 - day;

  //   const start = new Date(now);
  //   start.setDate(now.getDate() + diffToNextMonday);

  //   const end = new Date(start);
  //   end.setDate(start.getDate() + 6);

  //   const month = start.toLocaleString('default', { month: 'long' });
  //   const year = start.getFullYear();

  //   return `${month} ${start.getDate()}${getOrdinal(
  //     start.getDate()
  //   )} – ${end.getDate()}${getOrdinal(end.getDate())}, ${year}`;
  // };

  // const weeklyDateLabel =
  //   weeklyView === 'current'
  //     ? getCurrentWeekRange()
  //     : getNextWeekRange();

  return (
    <section className="w-full max-w-[1600px] mx-auto rounded-xl">
      <div className="flex justify-center mb-6">
        <SegmentedControl
          options={[
            { label: 'Current Week', value: 'current' },
            { label: 'Next Week', value: 'next' },
          ]}
          value={weeklyView}
          onChange={(val) => setWeeklyView(val as 'current' | 'next')}
        />
      </div>

      {isWeeklyUILoading
        ? null
        : textStatus === 'SUCCESS' &&
          !isGuestUser &&
          rashiCharts &&
          (rashiCharts.south || rashiCharts.north) && (
            <div className="grid grid-cols-1 md:grid-cols-4 md:gap-6 items-stretch rounded-xl border border-[#2DAAFD]">
              <div className="flex flex-col order-1 col-span-1 gap-6 md:order-none">
                <h4 className="font-spectral font-semibold text-[#001B6B] text-center text-lg mt-4 md:mt-0">
                  Rashi Charts
                </h4>

                <div className="flex flex-col overflow-hidden rounded-xl">
                  <div className="p-4">
                    {rashiCharts.south ? (
                      <img
                        src={rashiCharts.south}
                        alt="Weekly Rashi South Chart"
                        className="w-full h-full rounded-md border border-[#2DAAFD] p-2 cursor-pointer"
                        onClick={() =>
                          openModal(
                            [rashiCharts.south, rashiCharts.north],
                            'Rashi Chart (South Indian & North Indian)'
                          )
                        }
                      />
                    ) : (
                      <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
                        South Rashi chart not available
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    {rashiCharts.north ? (
                      <img
                        src={rashiCharts.north}
                        alt="Weekly Rashi North Chart"
                        className="w-full rounded-md border border-[#2DAAFD] p-2 cursor-pointer"
                        onClick={() =>
                          openModal(
                            [rashiCharts.south, rashiCharts.north],
                            'Rashi Chart (South Indian & North Indian)'
                          )
                        }
                      />
                    ) : (
                      <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
                        North Rashi chart not available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {ratings && (
                <div className="order-3 p-4 rounded-xl md:p-0 col-span1 md:col-span-2 md:order-none">
                  <RatingsBarChart ratings={ratings} custom={true} />

                  {(audioLoading || audioError || audioSrc) && (
                    <div className="order-4 block col-span-4 md:hidden xl:block md:mt-18 lg:mt-26 xl:mt-14 md:order-none">
                      {audioLoading ? (
                        <p className="text-center font-semibold text-[#082261] animate-pulse">
                          Loading your personalized prediction audio...
                        </p>
                      ) : audioError ? (
                        <div className="text-center">
                          <p className="font-medium text-red-500 mb-2">{audioError}</p>
                          {predictionId && (
                            <button
                              onClick={() => fetchAudioWithPolling(predictionId)}
                              className="px-4 py-2 bg-[#082261] text-white text-sm rounded-lg font-spectral hover:bg-[#2DAAFD] transition-colors"
                            >
                              Regenerate Audio
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          <AudioVisualizer audioSrc={audioSrc} />

                          <p className="mt-6 md:mt-10 text-center text-[12px] md:text-[16px] text-gray-800 font-medium">
                            Click Play to hear your personalized predictions.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col order-2 col-span-1 gap-6 md:order-none">
                <h4 className="font-spectral font-semibold text-[#001B6B] text-center text-lg mt-4 md:mt-0">
                  Transit Charts
                </h4>

                <div className="flex flex-col overflow-hidden rounded-xl">
                  <div className="p-4">
                    {predictionCharts.south ? (
                      <img
                        src={predictionCharts.south}
                        alt="Transit South Chart"
                        className="w-full aspect-square object-contain rounded-md border border-[#2DAAFD] p-2 cursor-pointer"
                        onClick={() =>
                          openModal(
                            [predictionCharts.south, predictionCharts.north],
                            'Transit Chart (South Indian & North Indian)'
                          )
                        }
                      />
                    ) : (
                      <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
                        South chart not available
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    {predictionCharts.north ? (
                      <img
                        src={predictionCharts.north}
                        alt="Transit North Chart"
                        className="w-full rounded-md border border-[#2DAAFD] p-2 cursor-pointer"
                        onClick={() =>
                          openModal(
                            [predictionCharts.south, predictionCharts.north],
                            'Transit Chart (South Indian & North Indian)'
                          )
                        }
                      />
                    ) : (
                      <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
                        North chart not available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="hidden col-span-4 md:block xl:hidden">
                {audioLoading ? (
                  <p className="text-center font-semibold text-[#082261] animate-pulse">
                    Loading your personalized prediction audio...
                  </p>
                ) : audioError ? (
                  <div className="text-center">
                    <p className="font-medium text-red-500 mb-2">{audioError}</p>
                    {predictionId && (
                      <button
                        onClick={() => fetchAudioWithPolling(predictionId)}
                        className="px-4 py-2 bg-[#082261] text-white text-sm rounded-lg font-spectral hover:bg-[#2DAAFD] transition-colors"
                      >
                        Regenerate Audio
                      </button>
                    )}
                  </div>
                ) : (
                  audioSrc && (
                    <div>
                      <AudioVisualizer audioSrc={audioSrc} />

                      <p className="mt-6 text-center text-[12px] md:text-[13px] text-gray-600 font-medium">
                        Click Play to hear your personalized predictions.
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

      <div className="mt-10 space-y-6 xl:col-span-2">
        {textStatus === 'FAILED' ? (
          <div className="text-center text-[#082261] font-spectral text-lg">
            Predictions are not visible, please click the Reload button.
            <br />
            <button
              onClick={reloadWeeklyPrediction}
              className="mt-4 px-6 py-2 bg-[#082261] text-white rounded-lg font-spectral"
            >
              Reload Prediction
            </button>
          </div>
        ) : textStatus === 'PENDING' ? (
          <div className="text-center text-[#082261] font-spectral text-lg">
            Decoding your unique planetary alignments to craft your personalized prediction. Please
            wait a moment...
          </div>
        ) : weeklyPrediction.length ? (
          weeklyPrediction.map((section, idx) => {
            if (section.title === 'HEADING') {
              return (
                <h2
                  key={idx}
                  className="font-spectral text-[#001B6B] font-bold text-[22px] uppercase text-center my-6 border-b border-[#001B6B]/20 pb-2"
                >
                  {section.content}
                </h2>
              );
            }

            let parsedContent: any = null;
            try {
              parsedContent = JSON.parse(section.content);
            } catch {}

            const isSectorLabel = (title: string) => {
              const normalized = title.trim().toLowerCase();
              return [
                'career',
                'wealth',
                'money',
                'relationships',
                'children',
                'health',
                'education',
                'overview',
                'general predictions',
                'personal creative life',
                'career & profession',
                'money & investments',
                'wealth & investments',
                'health & education',
                'children creative',
                'relationships & partnerships',
                'relationships partnerships',
                'children creative',
                'health education',
              ].some((lbl) => normalized.includes(lbl));
            };

            const isParentHeader = section.content === '' && section.title !== '';

            return (
              <section key={idx}>
                {isParentHeader ? (
                  <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide mt-8 underline decoration-[#001B6B]/30 underline-offset-4">
                    {section.title}
                  </h3>
                ) : !parsedContent && isSectorLabel(section.title) ? (
                  <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-3">
                    <span className="font-semibold text-[#001B6B]">{section.title}:</span>{' '}
                    {section.content}
                  </p>
                ) : (
                  <>
                    {section.title && section.title !== '' && (
                      <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide mt-4">
                        {section.title}
                      </h3>
                    )}

                    {parsedContent?.type === 'grouped-bullets' ? (
                      <div className="mt-3 space-y-4">
                        {parsedContent.groups.map(
                          (group: { subtitle: string; items: string[] }, gi: number) => (
                            <div key={gi}>
                              <h4 className="font-lato font-semibold text-[18px] text-[#001B6B] mb-1">
                                {group.subtitle}
                              </h4>
                              <ul className="pl-6 space-y-1 list-disc">
                                {group.items.map((item: string, i: number) => (
                                  <li
                                    key={i}
                                    className="font-lato text-[#333333] text-[17px] leading-relaxed"
                                  >
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                        )}
                      </div>
                    ) : parsedContent?.type === 'bullets' ? (
                      <ul className="pl-6 mt-3 space-y-2 list-disc">
                        {parsedContent.items.map((item: string, i: number) => {
                          const colonIndex = item.indexOf(':');
                          if (colonIndex > 0 && colonIndex < 35) {
                            const label = item.substring(0, colonIndex).trim();
                            const content = item.substring(colonIndex + 1).trim();

                            const shouldHighlight = [
                              'education',
                              'health',
                              'relationships',
                              'children',
                              'wealth & investments',
                              'career & profession',
                              'health & education',
                              'wealth and investments',
                              'career and profession',
                              'health and education',
                              'children creative',
                              'children creative endeavors',
                              "children's creative endeavors",
                              'relationships & partnerships',
                              'favourable days',
                              'caution days',
                              'general predictions',
                            ].includes(label.toLowerCase());

                            if (shouldHighlight) {
                              return (
                                <li
                                  key={i}
                                  className="font-lato text-[#333333] text-[18px] leading-relaxed"
                                >
                                  <span className="font-semibold text-[#001B6B]">{label}:</span>{' '}
                                  {content}
                                </li>
                              );
                            }
                          }
                          return (
                            <li
                              key={i}
                              className="font-lato text-[#333333] text-[18px] leading-relaxed"
                            >
                              {item}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-2">
                        {section.content}
                      </p>
                    )}
                  </>
                )}
              </section>
            );
          })
        ) : (
          <div className="text-center text-[#082261] font-spectral text-lg">
            No predictions available at the moment.
            <br />
            <button
              onClick={reloadWeeklyPrediction}
              className="mt-4 px-6 py-2 bg-[#082261] text-white rounded-lg font-spectral"
            >
              Reload Prediction
            </button>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-3xl"
      >
        <div className="flex flex-col gap-4 md:flex-row">
          {modalImages.map((img, i) => (
            <img
              key={i}
              src={img}
              alt="Chart"
              className={`w-full rounded-md ${i === 1 ? 'scale-[1.05]' : ''}`}
            />
          ))}
        </div>
      </Modal>
    </section>
  );
}
